/**
 * ESCHER FALLS — 全面クリア画面。METAGAME-SPEC §4。
 *
 * `#ui` の中に自前の DOM と `<style>` を持つ自己完結モジュール。
 * `hud.ts` が `#ui` を `replaceChildren` するので、**必ずその後に生成すること**。
 *
 * ## 演出の方針
 * 報酬設計の要は「達成の可視化」と「次の目標の提示」であって、演出の派手さではない。
 * したがって色を増やさず、**エッシャー調（紙と線、彩度は水だけ）を保ったまま**、
 * 段階的に開示する。一度に全部出すと山場が消える。
 *
 * 1. 紙に沈む
 * 2. 通した全ステージの縮図が並ぶ（`game/solutionPath.ts` が投影した折れ線）
 * 3. **順番に水が通っていく** — 自分が見つけた道すじが一望される
 * 4. 合計タイムと称号
 * 5. 解放の通知
 */

import type { Level } from '../core/types';
import type { RecordsData } from '../game/records';
import {
  grandTotalBestTime,
  mainLevelIds,
  mainTotalBestTime,
  rankOf,
} from '../game/progression';
import { buildThumbnail, type LevelThumbnail } from '../game/solutionPath';
import { t } from './i18n';
import { formatTime } from '../game/timer';
import { AD_MULTIPLIER } from '../game/wallet';

const STYLE_ID = 'ef-finale-style';
const SVG_NS = 'http://www.w3.org/2000/svg';

/** 縮図1つを描き切るのにかける時間 (ms)。 */
const DRAW_MS = 420;
/** 次の縮図に移るまでの間隔 (ms)。重ねぎみにして流れを作る。 */
const STAGGER_MS = 260;

const CSS = `
/* ブラウザ既定の <p> マージン（約11px）が残ると縦がスカスカになる。
   style.css のリセットはここまで面倒を見ていないので、自分で潰す。 */
.ef-fin p { margin: 0; }

.ef-fin {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  overflow-y: auto;
  padding: calc(var(--safe-top, 0px) + 40px) 20px calc(var(--safe-bottom, 0px) + 40px);
  background: var(--paper, #f2efe6);
  pointer-events: auto;
  /* 1. 紙に沈む */
  animation: ef-fin-in 900ms ease-out;
}
.ef-fin.is-on { display: flex; }
@keyframes ef-fin-in { from { opacity: 0; } to { opacity: 1; } }

/* 退出。**紙は残したまま墨だけが消える**ように見せる。
   下でステージ選択が同時に浮かび上がるので、重なって溶暗になる。
   いきなり消すと「画面が切り替わった」になり、帰ってきた感じが出ない。 */
.ef-fin.is-leaving {
  animation: none;
  opacity: 0;
  transition: opacity 700ms ease;
  pointer-events: none;
}

.ef-fin-title {
  font-family: var(--font-serif, serif);
  font-size: 34px;
  letter-spacing: 0.5em;
  text-indent: 0.5em;
  color: var(--ink, #2b2823);
}
.ef-fin-sub {
  font-size: 13px;
  letter-spacing: 0.2em;
  color: var(--ink-soft, #6b665c);
}

.ef-fin-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
  gap: 12px;
  width: 100%;
  max-width: 760px;
}
.ef-fin-cell { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.ef-fin-cell svg { width: 100%; height: 86px; overflow: visible; }
.ef-fin-cap {
  font-family: var(--font-serif, serif);
  font-size: 11px;
  letter-spacing: 0.08em;
  color: var(--ink-soft, #6b665c);
  text-align: center;
}
.ef-fin-time {
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  color: var(--water, #4a8c8a);
  opacity: 0;
  transition: opacity 300ms ease;
}
.ef-fin-time.is-on { opacity: 1; }

/* 紙吹雪。**色を増やさない。**
   参照した既存ゲームは原色を撒くが、この作品で彩度を持つのは水だけである。
   舞わせるのは「破れた紙片」と「墨の線」で、賑やかさは色ではなく
   **数と動きの不揃いさ**で出す。 */
.ef-fin-confetti {
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
}
.ef-fin-bit {
  position: absolute;
  top: -8vh;
  will-change: transform, opacity;
  animation: ef-fin-fall linear forwards;
}
@keyframes ef-fin-fall {
  from { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 1; }
  to   { transform: translate3d(var(--dx), 115vh, 0) rotate(var(--spin)); opacity: 0.9; }
}

/* 褒賞。祝いの画面で**いちばん大きい数字**にする。
   何をもらったのかが一目で分かることが、報酬の要件そのもの。 */
.ef-fin-prize { text-align: center; }
.ef-fin-prize[hidden] { display: none; }
.ef-fin-prize-label {
  font-size: 11px;
  letter-spacing: 0.34em;
  text-indent: 0.34em;
  color: var(--ink-soft, #6b665c);
}
.ef-fin-prize-amount {
  margin-top: 6px;
  font-family: var(--font-serif, serif);
  font-size: 46px;
  letter-spacing: 0.06em;
  font-variant-numeric: tabular-nums;
  color: var(--water, #4a8c8a);
}
/* 単位は JS で付ける（hud.ts の ef-drops と同じ理由）。 */
/* 広告の入口。**提供者が居なければ append しない**ので、
   ここに来る時点で押せることが保証されている（game/rewardedAd.ts 規則1）。 */
.ef-fin-multiply { margin-top: 14px; }

/* 踏破の称号。**これがこの画面の主役**なので、段位より大きく、枠で囲って
   「授かったもの」に見せる。色は水の色——彩度を持つのは水だけ、という原則を守る。 */
.ef-fin-award {
  margin-top: 4px;
  padding: 18px 30px;
  border: 1px solid var(--water, #4a8c8a);
  text-align: center;
}
.ef-fin-award[hidden] { display: none; }
.ef-fin-award-label {
  font-size: 11px;
  letter-spacing: 0.34em;
  text-indent: 0.34em;
  color: var(--ink-soft, #6b665c);
}
.ef-fin-award-name {
  margin-top: 10px;
  font-family: var(--font-serif, serif);
  font-size: 26px;
  letter-spacing: 0.28em;
  text-indent: 0.28em;
  color: var(--water, #4a8c8a);
}
.ef-fin-award-date {
  margin-top: 8px;
  font-size: 11px;
  letter-spacing: 0.12em;
  font-variant-numeric: tabular-nums;
  color: var(--ink-soft, #6b665c);
}

/* 4・5 は水が通り終わってから出す。先に出すと山場が消える。 */
.ef-fin-result, .ef-fin-unlock, .ef-fin-actions, .ef-fin-award, .ef-fin-prize {
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 600ms ease, transform 600ms ease;
}
.ef-fin-result.is-on, .ef-fin-unlock.is-on, .ef-fin-actions.is-on, .ef-fin-award.is-on,
.ef-fin-prize.is-on {
  opacity: 1;
  transform: none;
}

.ef-fin-total {
  font-family: var(--font-serif, serif);
  font-size: 22px;
  letter-spacing: 0.16em;
  font-variant-numeric: tabular-nums;
  color: var(--ink, #2b2823);
  text-align: center;
}
.ef-fin-rank {
  margin-top: 6px;
  font-family: var(--font-serif, serif);
  font-size: 17px;
  letter-spacing: 0.34em;
  text-indent: 0.34em;
  color: var(--accent, #c8622d);
  text-align: center;
}
.ef-fin-unlock {
  text-align: center;
  font-size: 13px;
  letter-spacing: 0.14em;
  color: var(--ink, #2b2823);
  border-top: 1px solid var(--ink-soft, #6b665c);
  padding-top: 14px;
  max-width: 460px;
}
.ef-fin-actions { display: flex; gap: 12px; }

@media (max-width: 480px) {
  .ef-fin-title { font-size: 26px; }
  .ef-fin-grid { grid-template-columns: repeat(auto-fill, minmax(88px, 1fr)); }
  .ef-fin-cell svg { height: 68px; }
}
`;

let styleRefCount = 0;

function injectStyles(): void {
  styleRefCount += 1;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function releaseStyles(): void {
  styleRefCount = Math.max(0, styleRefCount - 1);
  if (styleRefCount > 0) return;
  document.getElementById(STYLE_ID)?.remove();
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * 縮図1枚を SVG にする。
 *
 * `path` は `0..1` の正規化座標。**y は画面座標（上が正）なので描画時に反転する。**
 * 水の折れ線は `stroke-dasharray` で「引かれていく」アニメーションにする。
 * これが「自分が見つけた道すじ」の再生になる。
 */
function makeThumb(thumb: LevelThumbnail): {
  cell: HTMLElement;
  start: () => void;
  timeEl: HTMLElement;
} {
  const cell = el('div', 'ef-fin-cell');
  const svg = document.createElementNS(SVG_NS, 'svg');
  // viewBox は正方 + 余白。縦横比は path 側が保っているので、ここでは中央に置くだけ。
  svg.setAttribute('viewBox', '-0.12 -0.12 1.24 1.24');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  // 経路以外のポートは淡い点。構造の広がりが分かる程度に留める。
  for (const p of thumb.rest) {
    const dot = document.createElementNS(SVG_NS, 'circle');
    dot.setAttribute('cx', String(p.x));
    dot.setAttribute('cy', String(1 - p.y));
    dot.setAttribute('r', '0.022');
    dot.setAttribute('fill', 'var(--ink-soft, #6b665c)');
    dot.setAttribute('opacity', '0.28');
    svg.append(dot);
  }

  const d = thumb.path
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(4)},${(1 - p.y).toFixed(4)}`)
    .join(' ');
  const line = document.createElementNS(SVG_NS, 'path');
  line.setAttribute('d', d);
  line.setAttribute('fill', 'none');
  line.setAttribute('stroke', 'var(--water, #4a8c8a)');
  line.setAttribute('stroke-width', '0.055');
  line.setAttribute('stroke-linecap', 'round');
  line.setAttribute('stroke-linejoin', 'round');
  svg.append(line);

  const cap = el('p', 'ef-fin-cap', thumb.name);
  const timeEl = el('p', 'ef-fin-time');
  cell.append(svg, cap, timeEl);

  const start = (): void => {
    // getTotalLength は path が DOM に入ってからでないと 0 になる。
    const len = line.getTotalLength();
    if (len <= 0) return;
    line.style.strokeDasharray = String(len);
    line.style.strokeDashoffset = String(len);
    line.style.transition = `stroke-dashoffset ${DRAW_MS}ms linear`;
    // 次フレームまで待たないとブラウザが初期値を確定させず、アニメーションが飛ぶ。
    requestAnimationFrame(() => {
      line.style.strokeDashoffset = '0';
    });
  };

  // 初期状態は空。start() まで描かない。
  line.style.strokeDasharray = '1';
  line.style.strokeDashoffset = '1';
  return { cell, start, timeEl };
}

/**
 * どちらの祝いか。
 *
 * - `main` … 本編15面を通した。エクストラが開く。**次の目標を提示する画面**
 * - `all`  … 全25面を通した。**次が無いので、代わりに称号を渡す画面**
 *
 * 同じ部品を使い回しているのは、縮図に順番に水を通す演出がこの画面の核で、
 * それを二重に書きたくないため。違うのは「並べる面」「見出し」「締め方」だけ。
 */
export type FinaleMode = 'main' | 'all';

export interface FinaleCallbacks {
  /** 「二周目へ」。周回を切り替えてはじまりへ帰す。 */
  onExtra(): void;
  /**
   * エクストラへの誘い。**この画面でいちばん大事な口。**
   *
   * 本編を通した直後は、この作品で最も満足度が高く「もっと欲しい」が
   * 最大になる瞬間である。**そこに売り物の存在すら出していなかった。**
   * 差し出していたのは同じ21面の再走（二周目）だけだった。
   *
   * 文言は main.ts が決める（試遊がまだなら「試す」、済んでいれば「開く」、
   * 買った人には null を返してボタンごと消す）。**表示のたびに訊く。**
   */
  extraInviteLabel?(): string | null;
  onExtraInvite?(): void;
  /**
   * 「広告を見て倍にする」。**省略するとボタン自体を作らない。**
   * 広告の提供者が居ないときに押せないボタンを出さないための口
   * （`game/rewardedAd.ts` 規則1）。
   * 解決値は倍にしたあとの総取り分。
   */
  onMultiplyPrize?(): Promise<number | null>;
  /** 「もどる」。画面を閉じる。 */
  onClose(): void;
}

export interface Finale {
  readonly isOpen: boolean;
  show(
    levels: readonly Level[],
    records: RecordsData,
    mode?: FinaleMode,
    prize?: number,
  ): void;
  hide(): void;
  /**
   * 溶暗しながら閉じる。**`onReveal` は退出の始めに呼ぶ。**
   * 下に出したいもの（＝はじまりの画面）を先に置いてから消えることで、
   * 「切り替わった」ではなく「帰ってきた」に見える。
   */
  leave(onReveal: () => void): void;
  dispose(): void;
}

/**
 * 紙吹雪を撒く。
 *
 * 色は墨・薄墨・水・紙陰の4つだけ。**原色は使わない。**
 * 形は細長い紙片と短い線の2種。賑やかさは色ではなく
 * **数・大きさ・落ちる速さ・回る向きの不揃いさ**で作る。
 * 等速で同じ形が落ちると、祝いではなく画面効果に見える。
 *
 * 乱数は決定的にしない——ここは版ごとに違ってよい唯一の場所で、
 * 毎回同じ舞い方だと二度目に飽きる。
 */
const CONFETTI_COLORS = ['#2b2823', '#6b665c', '#4a8c8a', '#d8d3c4'];

function spawnConfetti(host: HTMLElement, count: number): void {
  host.replaceChildren();
  const frag = document.createDocumentFragment();
  for (let i = 0; i < count; i += 1) {
    const bit = document.createElement('span');
    bit.className = 'ef-fin-bit';
    const isSlip = Math.random() < 0.62;
    const w = isSlip ? 4 + Math.random() * 5 : 1.5 + Math.random() * 1.5;
    const h = isSlip ? 9 + Math.random() * 13 : 14 + Math.random() * 20;
    bit.style.width = `${w}px`;
    bit.style.height = `${h}px`;
    bit.style.left = `${Math.random() * 100}%`;
    bit.style.background = CONFETTI_COLORS[(Math.random() * CONFETTI_COLORS.length) | 0];
    bit.style.opacity = String(0.35 + Math.random() * 0.5);
    // 横流れと回転。左右どちらにも流す（片方向だと風に見えて祝いにならない）。
    bit.style.setProperty('--dx', `${(Math.random() - 0.5) * 34}vw`);
    bit.style.setProperty('--spin', `${(Math.random() - 0.5) * 1440}deg`);
    bit.style.animationDuration = `${2.4 + Math.random() * 2.8}s`;
    bit.style.animationDelay = `${Math.random() * 1.5}s`;
    frag.appendChild(bit);
  }
  host.appendChild(frag);
}

/** ISO 文字列を「2026年8月17日」に直す。読めない値なら空文字。 */
function formatAwardDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return t.dateLine(d);
}

export function createFinale(root: HTMLElement, callbacks: FinaleCallbacks): Finale {
  injectStyles();

  const panel = el('div', 'ef-fin');
  const title = el('p', 'ef-fin-title', t.clearTitle);
  const sub = el('p', 'ef-fin-sub');
  const grid = el('div', 'ef-fin-grid');

  const result = el('div', 'ef-fin-result');
  const total = el('p', 'ef-fin-total');
  const rank = el('p', 'ef-fin-rank');
  result.append(total, rank);

  const confetti = el('div', 'ef-fin-confetti');

  // 褒賞。何をもらったのかを、この画面でいちばん大きく見せる。
  const prizeBox = el('div', 'ef-fin-prize');
  const prizeLabel = el('p', 'ef-fin-prize-label', t.prizeLabel);
  const prizeAmount = el('p', 'ef-fin-prize-amount');
  const multiplyBtn = el('button', 'btn ef-fin-multiply');
  multiplyBtn.type = 'button';
  prizeBox.append(prizeLabel, prizeAmount);
  if (callbacks.onMultiplyPrize) prizeBox.append(multiplyBtn);

  const award = el('div', 'ef-fin-award');
  const awardLabel = el('p', 'ef-fin-award-label', t.awardLabel);
  const awardName = el('p', 'ef-fin-award-name', t.completionTitle);
  const awardDate = el('p', 'ef-fin-award-date');
  award.append(awardLabel, awardName, awardDate);

  const unlock = el('div', 'ef-fin-unlock');

  const actions = el('div', 'ef-fin-actions');
  // **誘いを先頭に置く。** 二周目は添え物へ降格した（同じ21面の再走を
  // 最大の功績への褒美として差し出す配置に無理があった）。
  const inviteBtn = el('button', 'btn btn--water ef-fin-invite', t.toExtra);
  const extraBtn = el('button', 'btn ef-fin-extra', t.toLoop2);
  const closeBtn = el('button', 'btn ef-fin-close', t.back);
  inviteBtn.type = 'button';
  extraBtn.type = 'button';
  closeBtn.type = 'button';
  if (callbacks.onExtraInvite) actions.append(inviteBtn);
  actions.append(extraBtn, closeBtn);

  panel.append(confetti, title, sub, grid, result, prizeBox, award, unlock, actions);
  panel.inert = true;
  root.append(panel);

  let open = false;
  /** 進行中の演出タイマ。閉じるときに必ず全部止める（閉じた後に発火すると幽霊が出る）。 */
  const timers: number[] = [];

  const bound: Array<{ el: EventTarget; type: string; fn: EventListener }> = [];
  const on = (target: EventTarget, type: string, fn: EventListener): void => {
    target.addEventListener(type, fn);
    bound.push({ el: target, type, fn });
  };

  function clearTimers(): void {
    for (const id of timers) window.clearTimeout(id);
    timers.length = 0;
  }

  function later(fn: () => void, ms: number): void {
    timers.push(window.setTimeout(fn, ms));
  }

  on(extraBtn, 'click', () => callbacks.onExtra());
  if (callbacks.onExtraInvite) {
    const onInvite = callbacks.onExtraInvite.bind(callbacks);
    on(inviteBtn, 'click', () => onInvite());
  }
  if (callbacks.onMultiplyPrize) {
    const multiply = callbacks.onMultiplyPrize.bind(callbacks);
    on(multiplyBtn, 'click', () => {
      // 二度押しで二重取りさせない。押した瞬間に閉じる。
      multiplyBtn.disabled = true;
      void multiply().then((total) => {
        if (total === null) {
          // 見なかった／読めなかった。**取り分はそのまま、詫びも責めもしない。**
          multiplyBtn.disabled = false;
          return;
        }
        prizeAmount.textContent = t.drops(total);
        multiplyBtn.hidden = true;
      });
    });
  }
  on(closeBtn, 'click', () => callbacks.onClose());
  on(window, 'keydown', (event) => {
    if (!open) return;
    if ((event as KeyboardEvent).key !== 'Escape') return;
    event.preventDefault();
    callbacks.onClose();
  });

  function show(
    levels: readonly Level[],
    records: RecordsData,
    mode: FinaleMode = 'main',
    prize = 0,
  ): void {
    clearTimers();

    const isAll = mode === 'all';
    // 誘いの可否は**開くたびに訊く。** 買った直後に開き直したときに
    // 売り文句が残っていてはいけない。
    const inviteLabel = callbacks.extraInviteLabel?.() ?? null;
    if (callbacks.onExtraInvite) {
      inviteBtn.hidden = isAll || inviteLabel === null;
      if (inviteLabel !== null) inviteBtn.textContent = inviteLabel;
    }

    // 踏破のあとは盤面へ戻す先が無い（もう通り終えている）ので、はじまりへ帰す。
    // 水が元の高さへ戻るこの作品で、最後に出発点へ戻るのは筋が通っている。
    closeBtn.textContent = isAll ? t.toStart : t.back;

    // 本編の祝いは本編15面だけを並べる。踏破は全25面を並べる。
    // 踏破でも本編だけを並べると、エクストラを越えた事実が絵から抜け落ちる。
    const ids = isAll ? levels.map((lv) => lv.id) : mainLevelIds(levels);
    const mains = levels.filter((lv) => ids.includes(lv.id));

    title.textContent = isAll ? t.allClearTitle : t.clearTitle;
    sub.textContent = isAll
      ? t.allClearBody(mains.length)
      : t.allClearBody(mains.length);

    const thumbs = mains.map(buildThumbnail);
    const built = thumbs.map(makeThumb);
    grid.replaceChildren(...built.map((b) => b.cell));

    // 褒賞。0 のときは枠ごと出さない（「雫 0」は報酬に見えない）。
    prizeBox.hidden = prize <= 0;
    prizeAmount.textContent = t.drops(prize);
    multiplyBtn.hidden = false;
    multiplyBtn.disabled = false;
    multiplyBtn.textContent = t.multiplyTimes(AD_MULTIPLIER);

    // 踏破のときだけ称号を出す。本編どまりでは渡さない——
    // 誰でも通る場所で渡すものは称号にならない。
    award.hidden = !isAll;

    // 踏破のあとに「エクストラへ」は要らない。もう通り終えている。
    // **演出の最後のタイマーではなくここで決める。** 出す/出さないは
    // 開いた瞬間に確定している事実であり、時間で変わるものではない。
    extraBtn.hidden = isAll;

    // 4・5 は水が通り終わってから
    result.classList.remove('is-on');
    prizeBox.classList.remove('is-on');
    award.classList.remove('is-on');
    unlock.classList.remove('is-on');
    actions.classList.remove('is-on');

    open = true;
    panel.classList.add('is-on');
    panel.inert = false;
    // 紙吹雪は開いた瞬間から。縮図に水が通り始めるより先に賑やかにする。
    spawnConfetti(confetti, isAll ? 90 : 60);

    // 2 → 3: 縮図に順番に水を通す
    built.forEach((b, i) => {
      later(() => {
        b.start();
        const rec = records.levels[mains[i].id];
        if (rec !== undefined) {
          b.timeEl.textContent = formatTime(rec.bestTimeMs);
          b.timeEl.classList.add('is-on');
        }
      }, 500 + i * STAGGER_MS);
    });

    const allDoneMs = 500 + built.length * STAGGER_MS + DRAW_MS + 200;

    later(() => {
      const sum = isAll ? grandTotalBestTime(levels, records) : mainTotalBestTime(levels, records);
      total.textContent = sum === null ? t.totalNone : t.totalTime(formatTime(sum));
      rank.textContent = rankOf(sum ?? Number.NaN, ids.length);
      result.classList.add('is-on');
    }, allDoneMs);

    if (prize > 0) {
      later(() => prizeBox.classList.add('is-on'), allDoneMs + 450);
    }

    if (isAll) {
      later(() => {
        const at = records.progress.allClearedAt;
        awardDate.textContent = at === null ? '' : formatAwardDate(at);
        award.classList.add('is-on');
      }, allDoneMs + 1000);
    }

    later(() => {
      // **本編の締めは「完結」ではなく「第一部の終わり」として書く。**
      // 「二周目が開いた」だけだと、ここで物語が閉じたように読める。
      // 満足して終わった人は続きを買わない。
      unlock.textContent = isAll
        // 踏破の締めは「次の目標」ではない。次がもう無いので、
        // 代わりに**残るもの**を告げる。ここで新しい遊びを匂わせると嘘になる。
        ? t.noteRank
        : inviteLabel === null
          ? t.noteLoop2
          : t.notePartOne;
      unlock.classList.add('is-on');
    }, allDoneMs + (isAll ? 1400 : 700));

    later(() => {
      actions.classList.add('is-on');
      closeBtn.focus();
    }, allDoneMs + (isAll ? 1900 : 1200));
  }

  function hide(): void {
    clearTimers();
    open = false;
    panel.classList.remove('is-on', 'is-leaving');
    panel.inert = true;
    // 紙吹雪の DOM を残さない。閉じても animation が回り続けて電池を食う。
    confetti.replaceChildren();
  }

  /** 溶暗しながら閉じる。演出の長さは CSS の transition と揃えること。 */
  const LEAVE_MS = 700;

  function leave(onReveal: () => void): void {
    if (!open) {
      onReveal();
      return;
    }
    clearTimers();
    panel.inert = true;
    // **先に下を出す。** そのうえで自分が消えるので、溶暗の間ずっと
    // 帰り先が見えている。順序を逆にすると一瞬だけ盤面が覗いて台無しになる。
    onReveal();
    panel.classList.add('is-leaving');
    window.setTimeout(hide, LEAVE_MS);
  }

  return {
    get isOpen(): boolean {
      return open;
    },
    show,
    hide,
    leave,
    dispose(): void {
      clearTimers();
      for (const b of bound) b.el.removeEventListener(b.type, b.fn);
      bound.length = 0;
      panel.remove();
      releaseStyles();
    },
  };
}
